import type {Cell} from 'notebook/tables/constants/CellConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {selectionStraddlesFreezeBoundary} from 'notebook/tables/core-ui/FreezeColumnUtils';
import * as ast from 'notebook/tables/excess/AST';
import {FullTableMap} from 'notebook/tables/excess/EvaluationConstants';
import tokenizer from 'notebook/tables/excess/Tokenizer';
import * as excessUtil from 'notebook/tables/excess/Util';
import {computeHasTextInMergedRegion} from 'notebook/tables/store/logic/TableMergedCellLogic';

/**
 * The result of validating a merge of the current selection.
 */
export interface MergeValidation {
  /**
   * Whether the selection spans across the freeze boundary. Merging is not
   * allowed in this case; the caller should surface the corresponding warning.
   */
  straddlesFreezeBoundary: boolean;

  /**
   * Whether the merge would discard cell values (any cell other than the
   * top-leftmost cell has non-empty text). The caller should confirm with the
   * user before proceeding.
   */
  requiresConfirmation: boolean;
}

/**
 * Validates whether the given (already merge-expanded) selection box can be
 * merged into a single cell.
 *
 * This mirrors the validation previously performed inline in
 * `TableEditable.mergeSelection()`: it does not perform the merge itself, it
 * only reports whether the selection straddles the freeze boundary (in which
 * case merging is disallowed) and whether merging would lose data (in which
 * case the caller should confirm with the user).
 *
 * @param table - The raw table cells.
 * @param box - The merge-expanded selection box.
 * @param freezeBoundary - The absolute freeze boundary column index.
 */
export function computeMergeValidation(
  table: Array<Array<Cell>>,
  box: MultiSelectionBox,
  freezeBoundary: number
): MergeValidation {
  return {
    straddlesFreezeBoundary: selectionStraddlesFreezeBoundary(box.minJ, box.maxJ, freezeBoundary),
    requiresConfirmation: computeHasTextInMergedRegion(table, box),
  };
}

/**
 * Computes whether the table supports merging cells.
 *
 * This mirrors the original `TableEditable.supportsMerge()`, which checked that
 * both `tableMutations.mergeRegion` and `tableMutations.unmergeRegion` were
 * non-null. Those mutation methods are gated solely by the `disableMergingCells`
 * prop (`disableMergingCells ? null : tableOps.mergeTableRegion`), and the
 * underlying `tableOps` merge/unmerge functions are always defined. Merge is
 * therefore supported exactly when merging is not disabled.
 *
 * @param disableMergingCells - Whether merging cells is disabled for this table
 */
export function computeSupportsMerge(disableMergingCells: boolean): boolean {
  return !disableMergingCells;
}

/**
 * Returns whether the cell at (`row`, `column`) is a "subordinate" (child) cell of the merged region
 * described by `box`. Every cell inside the box other than its top-left anchor cell is subordinate: once
 * merged, those cells are emptied and references to them evaluate to empty.
 */
function isSubordinateCell(box: MultiSelectionBox, row: number, column: number): boolean {
  const withinBox = row >= box.minI && row <= box.maxI && column >= box.minJ && column <= box.maxJ;
  const isAnchor = row === box.minI && column === box.minJ;
  return withinBox && !isAnchor;
}

/**
 * Returns whether the rectangular range spanning (`rowA`, `colA`) to (`rowB`, `colB`) overlaps at least
 * one subordinate cell of the merged region described by `box`. Range endpoints may be given in any order.
 */
function rangeCoversSubordinateCell(
  box: MultiSelectionBox,
  rowA: number,
  rowB: number,
  colA: number,
  colB: number
): boolean {
  const overlapMinRow = Math.max(Math.min(rowA, rowB), box.minI);
  const overlapMaxRow = Math.min(Math.max(rowA, rowB), box.maxI);
  const overlapMinCol = Math.max(Math.min(colA, colB), box.minJ);
  const overlapMaxCol = Math.min(Math.max(colA, colB), box.maxJ);
  if (overlapMinRow > overlapMaxRow || overlapMinCol > overlapMaxCol) {
    // The range does not intersect the merged region at all.
    return false;
  }
  const overlapCellCount = (overlapMaxRow - overlapMinRow + 1) * (overlapMaxCol - overlapMinCol + 1);
  // The anchor at (minI, minJ) is the only non-subordinate cell in the box. If the overlap is exactly that
  // single cell, no subordinate cell is covered.
  if (overlapCellCount === 1 && overlapMinRow === box.minI && overlapMinCol === box.minJ) {
    return false;
  }
  return true;
}

/**
 * Returns whether a single formula (stored in a cell's `text`, e.g. `=C2+SUM(C1:C4)`) references any
 * subordinate cell of the merged region described by `box`.
 *
 * References are resolved relative to the formula's table (`formulaTableName`): an unqualified reference (e.g.
 * `C2`) targets the table containing the formula, while a table-qualified reference (e.g. `Data!C2`) targets
 * the named table. Only references into `mergedTableName` are considered, and only within the same document
 * (since the merge happens within the current document).
 */
function formulaReferencesSubordinateCell(
  formulaText: string,
  formulaTableName: string,
  mergedTableName: string,
  box: MultiSelectionBox
): boolean {
  const formulaBody = excessUtil.getFormulaText(formulaText);
  if (formulaBody == null) {
    return false;
  }
  // `partialTokenize` never throws: it returns whatever prefix of the formula tokenized cleanly, which is
  // all we need to inspect cell references.
  const {tokens} = tokenizer.partialTokenize(formulaBody, {relative: false});
  if (tokens == null) {
    return false;
  }
  for (const token of tokens) {
    if (!token.isCellRef()) {
      continue;
    }
    const {node} = token;
    if (node instanceof ast.Cell) {
      if (node.id.document != null) {
        continue;
      }
      const referencedTable = node.id.table ?? formulaTableName;
      if (referencedTable === mergedTableName && isSubordinateCell(box, node.id.row, node.id.column)) {
        return true;
      }
    } else if (node instanceof ast.CellRange) {
      if (node.start.document != null) {
        continue;
      }
      const referencedTable = node.start.table ?? formulaTableName;
      if (
        referencedTable === mergedTableName &&
        rangeCoversSubordinateCell(box, node.start.row, node.end.row, node.start.column, node.end.column)
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Detects formulas that reference subordinate (child) cells of a merged region being created.
 *
 * When a range is merged, every cell other than the top-left anchor is emptied, so any formula that
 * references one of those subordinate cells — directly (`C2`) or via a range (`C1:C4`) — may silently
 * become incorrect. This scans every table in `tableMap` and returns, in note order, the names of the
 * tables that contain at least one such formula, so the caller can warn the user before merging.
 *
 * @param tableMap - All tables in the current entry, keyed by table name.
 * @param mergedTableName - The name of the table whose cells are being merged.
 * @param box - The (merge-expanded) selection box being merged.
 */
export function findTablesReferencingMergedChildCells(
  tableMap: FullTableMap,
  mergedTableName: string,
  box: MultiSelectionBox
): Array<string> {
  const tablesWithReferences: Array<string> = [];
  for (const [tableName, table] of Object.entries(tableMap)) {
    if (table?.table == null) {
      continue;
    }
    const hasReference = table.table.some((cellRow) =>
      cellRow.some(
        (cell) =>
          cell?.text != null && formulaReferencesSubordinateCell(cell.text, tableName, mergedTableName, box)
      )
    );
    if (hasReference) {
      tablesWithReferences.push(tableName);
    }
  }
  return tablesWithReferences;
}
